import {datadogRum} from '@datadog/browser-rum';

import {assert} from 'Asserts';
import {distribution} from 'metrics/Metrics';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import SidepanelStore from 'nav/sidepanel/SidepanelStore';

import {
  clearAllTimingEventsWithDomainAndFeature,
  clearProfilerTimingEvent,
  getProfilerEventValue,
} from './BenchlingProfiler';
import getCurrentSearchInfo, {getCurrentSearchId} from './getCurrentSearchInfo';
import {toSeconds} from './PerformanceUtils';
import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_SEARCH_EVENTS_VERSION = 2;
function addDatadogAction(name: string, context: object): void {
  datadogRum.addAction(name, {...context, versionTag: PROFILER_SEARCH_EVENTS_VERSION});
}

export enum ProfilerSearchFeatures {
  DROPDOWN_SEARCH = 'dropdownSearch',
  SIDEPANEL_SEARCH = 'sidepanelSearch',
}
const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerSearchFeatures]: Function} = {
  [ProfilerSearchFeatures.DROPDOWN_SEARCH]: processDropdownSearch,
  [ProfilerSearchFeatures.SIDEPANEL_SEARCH]: processSidepanelSearch,
};

enum ProfilerSearchActions {
  SEARCH_STARTED = 'searchStarted',
  SEARCH_RESULTS_RETURNED = 'searchResultsReturned',
  SEARCH_RESULTS_RENDERED = 'searchResultsRendered',
}

export const DROPDOWN_SEARCH_STARTED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SEARCH,
  feature: ProfilerSearchFeatures.DROPDOWN_SEARCH,
  action: ProfilerSearchActions.SEARCH_STARTED,
};
export const DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT: ProfilerEvent = {
  domain: ProfilerEventDomain.SEARCH,
  feature: ProfilerSearchFeatures.DROPDOWN_SEARCH,
  action: ProfilerSearchActions.SEARCH_RESULTS_RENDERED,
};
export function composeSidepanelSearchStartedEvent(
  searchId: string | undefined = getCurrentSearchId()
): ProfilerEvent {
  return composeSidepanelSearchProfilerEvent(ProfilerSearchActions.SEARCH_STARTED, searchId);
}
export function composeSidepanelSearchResultsReturnedEvent(
  searchId: string | undefined = getCurrentSearchId()
): ProfilerEvent {
  return composeSidepanelSearchProfilerEvent(ProfilerSearchActions.SEARCH_RESULTS_RETURNED, searchId);
}
export function composeSidepanelSearchResultsRenderedEvent(
  searchId: string | undefined = getCurrentSearchId()
): ProfilerEvent {
  return composeSidepanelSearchProfilerEvent(ProfilerSearchActions.SEARCH_RESULTS_RENDERED, searchId);
}

function composeSidepanelSearchProfilerEvent(
  actionBase: ProfilerSearchActions,
  searchId: string | undefined
): ProfilerEvent {
  return {
    domain: ProfilerEventDomain.SEARCH,
    feature: ProfilerSearchFeatures.SIDEPANEL_SEARCH,
    action: searchId ? `${actionBase}_${searchId}` : actionBase,
  };
}

export function cleanUpSearchProfilerEvents(): void {
  clearAllTimingEventsWithDomainAndFeature(
    ProfilerEventDomain.SEARCH,
    ProfilerSearchFeatures.SIDEPANEL_SEARCH
  );
}

export function processSearchEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerSearchFeatures;
  assert(
    Object.values(ProfilerSearchFeatures).includes(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature]();
}

/**
 * Phase 1 of Search is the actual search call up to SearchResultsModelManager.ts, where the results are
 * returned by the backend
 *
 * Phase 2 of Search is everything that comes afterwards until the results are displayed to the user, such as
 * the GraphQL calls
 *
 * This function sends the total time (Phase 1 + Phase 2), as well as the timing for each individual time
 * up to Datadog.
 */
function processSidepanelSearch(): void {
  const SIDEPANEL_SEARCH_STARTED_EVENT = composeSidepanelSearchStartedEvent();
  const SIDEPANEL_SEARCH_RESULTS_RETURNED_EVENT = composeSidepanelSearchResultsReturnedEvent();
  const SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT = composeSidepanelSearchResultsRenderedEvent();
  const searchStartedTime = getProfilerEventValue(SIDEPANEL_SEARCH_STARTED_EVENT);
  const searchResultsReturnedTime = getProfilerEventValue(SIDEPANEL_SEARCH_RESULTS_RETURNED_EVENT);
  const searchResultsRenderedTime = getProfilerEventValue(SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT);

  if (searchResultsRenderedTime != null && searchStartedTime == null && searchResultsReturnedTime == null) {
    clearProfilerTimingEvent(SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT);
  }

  if (searchResultsRenderedTime != null && searchStartedTime != null && searchResultsReturnedTime != null) {
    const totalTime = toSeconds(searchResultsRenderedTime.timestamp - searchStartedTime.timestamp);
    const phase1Time = toSeconds(searchResultsReturnedTime.timestamp - searchStartedTime.timestamp);
    const phase2Time = toSeconds(searchResultsRenderedTime.timestamp - searchResultsReturnedTime.timestamp);

    const {section, searchType, apiCall} = getCurrentSearchInfo();

    addDatadogAction('Search Listing: E2E', {
      time: totalTime,
      section,
      searchType,
      apiCall,
    });
    addDatadogAction('Search Listing: Start Search to Results Returned', {
      time: phase1Time,
      section,
      searchType,
      apiCall,
    });
    addDatadogAction('Search Listing: Results Returned to Results Rendered', {
      time: phase2Time,
      section,
      searchType,
      apiCall,
    });

    distribution('perf.sidepanel_search_e2e', totalTime);

    clearProfilerTimingEvent(SIDEPANEL_SEARCH_STARTED_EVENT);
    clearProfilerTimingEvent(SIDEPANEL_SEARCH_RESULTS_RETURNED_EVENT);
    clearProfilerTimingEvent(SIDEPANEL_SEARCH_RESULTS_RENDERED_EVENT);
  }
}

function processDropdownSearch(): void {
  const searchStartedTime = getProfilerEventValue(DROPDOWN_SEARCH_STARTED_EVENT);
  const searchResultsRenderedTime = getProfilerEventValue(DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT);

  if (searchResultsRenderedTime != null && searchStartedTime != null) {
    const totalTime = toSeconds(searchResultsRenderedTime.timestamp - searchStartedTime.timestamp);

    if (totalTime < 0) {
      // If the time is negative, there was a leftover rendered event timing from a previous search
      // This clears the state, and doesn't send the metric
      clearProfilerTimingEvent(DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT);
      return;
    }

    const searchType = searchResultsRenderedTime.extra?.searchType;
    const apiCall = searchResultsRenderedTime.extra?.url;

    addDatadogAction('Dropdown Search: E2E', {
      time: totalTime,
      searchType,
      apiCall,
    });

    distribution('perf.dropdown_search_e2e', totalTime);

    clearProfilerTimingEvent(DROPDOWN_SEARCH_STARTED_EVENT);
    clearProfilerTimingEvent(DROPDOWN_SEARCH_RESULTS_RENDERED_EVENT);
  }
}
